import { getCheckpoint } from '../data/curriculum';
import type {
  CheckpointRecord,
  CurriculumTierId,
  TrinRecord,
  UserProfile,
} from '../types';
import { allTrinIdsInTier, firstTrinIdInTier } from './curriculum';

/** V3.1 niveau-1-trin-N → V3.2 trin ids. */
const OLD_NIVEAU_1_TRIN_MAP: Record<string, string | string[]> = {
  'niveau-1-trin-1': 'niveau-1-trin-1',
  'niveau-1-trin-2': 'niveau-1-trin-2',
  'niveau-1-trin-3': 'niveau-2-trin-1',
  'niveau-1-trin-4': 'niveau-1-trin-3',
  'niveau-1-trin-5': ['niveau-2-trin-4', 'niveau-3-trin-1'],
  'niveau-1-trin-6': 'niveau-3-trin-2',
  'niveau-1-trin-7': 'niveau-3-trin-3',
  'niveau-1-trin-8': 'niveau-3-trin-4',
  'niveau-1-trin-9': 'niveau-2-trin-3',
  'niveau-1-trin-10': 'niveau-2-trin-2',
  'niveau-1-trin-11': 'niveau-4-trin-1',
  'niveau-1-trin-12': 'niveau-4-trin-2',
  'niveau-1-trin-13': 'niveau-4-trin-3',
  'niveau-1-trin-14': 'niveau-4-trin-4',
  'niveau-1-trin-15': 'niveau-4-trin-5',
};

const LEGACY_SPRINT_TRIN = 'niveau-1-trin-16';
const FULL_ALPHABET_CHECKPOINT = 'checkpoint-after-niveau-4';
const OLD_ALPHABET_CHECKPOINT = 'checkpoint-after-niveau-1';

const LETTER_TIERS: CurriculumTierId[] = [
  'niveau-1',
  'niveau-2',
  'niveau-3',
  'niveau-4',
];

function expandTrinId(id: string): string[] {
  const mapped = OLD_NIVEAU_1_TRIN_MAP[id];
  if (!mapped) return [id];
  return Array.isArray(mapped) ? mapped : [mapped];
}

export function migrateTrinIdV32(id: string): string {
  if (id === LEGACY_SPRINT_TRIN) return id;
  const expanded = expandTrinId(id);
  return expanded[0] ?? id;
}

export function migrateTrinIdListV32(ids: string[]): string[] {
  const out = new Set<string>();
  for (const id of ids) {
    if (id === LEGACY_SPRINT_TRIN) continue;
    for (const mapped of expandTrinId(id)) out.add(mapped);
  }
  return [...out];
}

function hadFullOldNiveau1(completedTrinIds: string[]): boolean {
  if (
    completedTrinIds.includes('niveau-1-trin-15') ||
    completedTrinIds.includes(LEGACY_SPRINT_TRIN)
  ) {
    return true;
  }
  const required = Object.keys(OLD_NIVEAU_1_TRIN_MAP);
  return required.every((id) => completedTrinIds.includes(id));
}

function mergeCheckpointRecords(
  a: CheckpointRecord | undefined,
  b: CheckpointRecord | undefined,
): CheckpointRecord | undefined {
  if (!a) return b;
  if (!b) return a;
  return {
    bestWords: Math.max(a.bestWords, b.bestWords),
    attempts: a.attempts + b.attempts,
    lastPlayedAt: a.lastPlayedAt > b.lastPlayedAt ? a.lastPlayedAt : b.lastPlayedAt,
  };
}

/** Migrate V3.1 curriculum (single niveau-1) to V3.2 (four letter tiers). */
export function migrateUserToV32(
  user: UserProfile,
  preMigrationCompleted?: string[],
): UserProfile {
  const sourceCompleted = preMigrationCompleted ?? user.completedTrinIds;
  const hadOldIds = sourceCompleted.some(
    (id) => id in OLD_NIVEAU_1_TRIN_MAP || id === LEGACY_SPRINT_TRIN,
  );
  const onOldCurrent =
    user.currentTrinId in OLD_NIVEAU_1_TRIN_MAP ||
    user.currentTrinId === LEGACY_SPRINT_TRIN;

  if (!hadOldIds && !onOldCurrent) {
    return user;
  }

  let completedTrinIds = migrateTrinIdListV32(sourceCompleted);
  let checkpointRecords = { ...(user.checkpointRecords ?? {}) };
  const trinRecords = { ...user.trinRecords };

  if (sourceCompleted.includes(LEGACY_SPRINT_TRIN)) {
    const legacy = trinRecords[LEGACY_SPRINT_TRIN];
    if (legacy) {
      const bestWords = (legacy as TrinRecord & { bestWords?: number }).bestWords ?? 0;
      if (legacy.attempts > 0 || bestWords > 0) {
        const migrated: CheckpointRecord = {
          bestWords,
          attempts: legacy.attempts,
          lastPlayedAt: legacy.lastPlayedAt,
        };
        const merged = mergeCheckpointRecords(
          checkpointRecords[FULL_ALPHABET_CHECKPOINT],
          migrated,
        );
        if (merged) checkpointRecords[FULL_ALPHABET_CHECKPOINT] = merged;
      }
    }
    delete trinRecords[LEGACY_SPRINT_TRIN];
    completedTrinIds = completedTrinIds.filter((id) => id !== LEGACY_SPRINT_TRIN);
  }

  const oldAlphabetCp = checkpointRecords[OLD_ALPHABET_CHECKPOINT];
  if (oldAlphabetCp && hadFullOldNiveau1(sourceCompleted)) {
    const mergedAlphabet = mergeCheckpointRecords(
      checkpointRecords[FULL_ALPHABET_CHECKPOINT],
      oldAlphabetCp,
    );
    if (mergedAlphabet) checkpointRecords[FULL_ALPHABET_CHECKPOINT] = mergedAlphabet;
    delete checkpointRecords[OLD_ALPHABET_CHECKPOINT];
  }

  if (hadFullOldNiveau1(sourceCompleted)) {
    for (const tierId of LETTER_TIERS) {
      for (const id of allTrinIdsInTier(tierId)) {
        if (!completedTrinIds.includes(id)) completedTrinIds.push(id);
      }
    }
  }

  let currentTrinId = user.currentTrinId;
  if (onOldCurrent) {
    if (currentTrinId === LEGACY_SPRINT_TRIN) {
      const cp = getCheckpoint(FULL_ALPHABET_CHECKPOINT);
      currentTrinId =
        (cp && firstTrinIdInTier(cp.beforeTierId)) ??
        firstTrinIdInTier('niveau-5') ??
        currentTrinId;
    } else {
      const mapped = expandTrinId(currentTrinId);
      currentTrinId =
        mapped.find((id) => !completedTrinIds.includes(id)) ?? mapped[mapped.length - 1];
    }
  }

  let completedTierIds = [...user.completedTierIds];
  if (hadFullOldNiveau1(sourceCompleted)) {
    for (const tierId of LETTER_TIERS) {
      if (!completedTierIds.includes(tierId)) completedTierIds.push(tierId);
    }
  }

  for (const [oldId, rec] of Object.entries({ ...trinRecords })) {
    if (!(oldId in OLD_NIVEAU_1_TRIN_MAP)) continue;
    for (const newId of expandTrinId(oldId)) {
      const prev = trinRecords[newId];
      if (!prev || rec.lastPlayedAt > prev.lastPlayedAt) {
        trinRecords[newId] = { ...rec };
      }
    }
    delete trinRecords[oldId];
  }

  return {
    ...user,
    currentTrinId,
    completedTrinIds,
    completedTierIds,
    checkpointRecords,
    trinRecords,
  };
}
